import isNull from 'lodash/isNull.js';
import merge from 'lodash/merge.js';
import unset from 'lodash/unset.js';

import axios from '@/plugins/axios.js';
import Quiz from '@/store/modules/quizzes/Quiz.js';
import Collection from '@/store/quill/Collection.js';
import EdverumTable from '@/store/utilities/EdverumTable.js';

const collectionHelpers = EdverumTable.collection('quiz');

export default merge(Collection('Quizzes', Quiz), {
    state: {
        sort: {
            id: null,
            title: null,
            created_at: null,
            updated_at: null,
        },

        type: null,
    },

    getters: {
        ...collectionHelpers.getters,

        all(state) {
            return () => {
                const items = { ...state };
                unset(items, 'loading');
                unset(items, 'sort');
                unset(items, 'paginate');
                unset(items, 'type');

                return items;
            };
        },
    },

    actions: {
        clearSort: collectionHelpers.actions.clearSort,
        paginateAndFetch: collectionHelpers.actions.paginateAndFetch,

        fetch: collectionHelpers.actions.fetch(({ context, queryBuilder }) => {
            const { getters, rootGetters } = context;
            const { type } = context.state;

            queryBuilder
                .include(['questions', 'tags', 'groups', 'courseCodes'])
                .sort(getters.queryBuilderSorting)
                .setPagination(getters.paginate);

            if (!rootGetters['QuizzesView/hasViewArchive']) {
                if (type === 'survey') {
                    queryBuilder.filters([{ key: 'latest_version_by_type', value: type }]);
                }

                if (isNull(type)) {
                    queryBuilder.filters([{ key: 'latest_version_by_type', value: 1 }]);
                }
            }

            if (rootGetters['QuizzesView/hasQuery']) {
                queryBuilder.raw({
                    search: rootGetters['QuizzesView/searchQuery'],
                });
            }

            if (rootGetters['QuizzesView/hasTagSearch']) {
                queryBuilder.raw({
                    tags: rootGetters['QuizzesView/searchTag'],
                });
            }

            if (rootGetters['QuizzesView/hasGroupSearch']) {
                queryBuilder.raw({
                    groups: rootGetters['QuizzesView/searchGroup'],
                });
            }

            if (rootGetters['QuizzesView/hasViewArchive']) {
                queryBuilder.raw({
                    view_archived: rootGetters['QuizzesView/searchViewArchive'],
                });
            }

            queryBuilder.append('version_number');
            queryBuilder.append('quiz_attempt_count');
            queryBuilder.append('latest_quiz_attempt');
        }),

        fetchOne: collectionHelpers.actions.fetchOne(({ queryBuilder }) => {
            queryBuilder.include(['questions', 'activities', 'activityTemplates', 'tags', 'groups', 'courseCodes']);
        }),

        /**
         * @param context
         * @param filter
         * @return {Promise<any>}
         */
        async filterAndFetch(context, filter) {
            try {
                await context.dispatch('filter', filter);
                await context.dispatch('purge');
                await context.dispatch('fetch');
            } catch (error) {
                return error;
            }
        },

        addQuiz: collectionHelpers.actions.addModel(),

        updateQuiz: collectionHelpers.actions.updateModel(),

        updateTags: collectionHelpers.actions.updateModelTags(),

        updateGroups: collectionHelpers.actions.updateModelGroups(),

        removeChecked: collectionHelpers.actions.removeChecked(),

        async syncCourseCodes(context, { quiz, paramKey, courseCodes }) {
            await context.dispatch('AutoSave/setSaving', `${Collection}/updateGroups`, { root: true });

            await axios.put(`/api/quizzes/${quiz.id}`, {
                ...quiz,
                [paramKey]: courseCodes,
            });

            await context.dispatch('AutoSave/setSaved', `${Collection}/updateGroups`, { root: true });
        },
    },

    mutations: {
        EDIT: (state, { field, value }) => {
            state[field] = value;
        },
        UPDATE_TAGS: collectionHelpers.mutations.UPDATE_TAGS,
        UPDATE_GROUPS: collectionHelpers.mutations.UPDATE_GROUPS,
        CLEAR_SORTING: collectionHelpers.mutations.CLEAR_SORTING,
    },
});
